import { distanceLabel, sellerRadiusMiles } from './offeringFit';
import type { MsaasJobListingResponse } from './types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Dates arrive as calendar days (YYYY-MM-DD); format them without a timezone shifting the day.
const parts = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return { year, month: MONTHS[month - 1], day };
};

/** "Mar 1–Dec 31 2026", "Mar 1 2026–Jan 4 2027", or "From Mar 1 2026" when it has no end. */
export const availabilityWindow = (start?: string | null, end?: string | null): string => {
  if (!start) return 'Dates TBD';
  const from = parts(start);
  if (!end) return `From ${from.month} ${from.day} ${from.year}`;
  const to = parts(end);
  return from.year === to.year
    ? `${from.month} ${from.day}–${to.month} ${to.day} ${to.year}`
    : `${from.month} ${from.day} ${from.year}–${to.month} ${to.day} ${to.year}`;
};

/**
 * The API's boardStatus is CERTIFIED when the provider holds a current board certification and
 * NOT_CERTIFIED otherwise; a buyer reads it as words, never as the enum.
 */
export const boardStatusLabel = (status: string | null | undefined): string | null => {
  if (status === 'CERTIFIED') return 'Board certified';
  if (status === 'NOT_CERTIFIED') return 'Not board certified';
  return null;
};

export type LocationNote =
  | { kind: 'none' }
  | { kind: 'add-location' }
  | { kind: 'distance'; text: string; flags: Array<{ key: string; text: string }> };

/**
 * Line 2 of a result: the distance, or why there is none (DD13). A remote listing never shows
 * one; an on-site or hybrid one asks for the buyer's location first.
 */
export const locationNote = (
  listing: MsaasJobListingResponse,
  buyerHasLocation: boolean
): LocationNote => {
  if (listing.workLocation === 'REMOTE') return { kind: 'none' };
  if (!buyerHasLocation) return { kind: 'add-location' };
  const fit = listing.fit;
  const flags: Array<{ key: string; text: string }> = [];
  if (fit?.flags.includes('OUTSIDE_TRAVEL_RANGE') && fit.distanceMiles != null)
    flags.push({
      key: 'OUTSIDE_TRAVEL_RANGE',
      text: `Outside travel range · ${Math.round(fit.distanceMiles)} mi`,
    });
  if (fit?.flags.includes('NO_PROVIDERS_NEARBY')) {
    const radius = sellerRadiusMiles(listing);
    flags.push({
      key: 'NO_PROVIDERS_NEARBY',
      text: radius != null ? `No providers within ${radius} mi` : 'No providers nearby',
    });
  }
  const text = distanceLabel(fit);
  return text || flags.length > 0
    ? { kind: 'distance', text: text ?? '', flags }
    : { kind: 'none' };
};
